import React, { useState, useMemo } from 'react';
import { register, RegisterData, checkRegisterEmailAddress } from '@/services/users';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidationSummary, ValidationForm } from '@/components/forms/validation';
import { loadUser } from '@/state/global/creators';
import { useAppDispatch, useGetCorrectLanguageCodeForPhoneInput, useValidationManager } from '@/utils/hooks';
import UserPageInput, { UserPageInputWrapper } from './UserPageInput';
import { InvitationWithToken, TranslatableText, EmailValidity } from '@/types';
import theme from './RegisterForm.module.scss';
import { classnames } from '@/utils/html';
import RegisterFormEmailWrapper from './RegisterFormEmailWrapper';
import PasswordValidation from './PasswordValidation';
import { getDefaultPasswordRequirements, PasswordValidator } from '@/utils/password';
import {
    COUNTRY_CODES,
    isSalutatable,
    PhoneInput,
    USER_SALUTATION_OPTIONS,
    USER_TITLE_OPTIONS,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Button as RubusButton } from '@ygtechnologies/rubus-ui';
import UserPageSelect from './UserPageSelect';

type WithStateProps = {
    invitation?: InvitationWithToken;
    onRegister?: () => void;
};

export const RegisterWithState: React.FC<WithStateProps> = ({ invitation, onRegister: onRegisterCallback }) => {
    function getInviteValueOrDefault<T extends InvitationWithToken, K extends keyof T>(
        invitation: T | undefined,
        field: K
    ): T[K] | '' {
        if (!invitation) {
            return '' as T[K];
        }

        const value = invitation[field];
        return (value !== undefined && value !== null ? value : '') as T[K];
    }

    const [registerData, setRegisterData] = useState<RegisterData>({
        email: getInviteValueOrDefault(invitation, 'inviteeEmail'),
        firstName: getInviteValueOrDefault(invitation, 'inviteeFirstName'),
        inviteToken: invitation ? invitation.token : null,
        lastName: getInviteValueOrDefault(invitation, 'inviteeLastName'),
        salutation: getInviteValueOrDefault(invitation, 'inviteeSalutation') || null,
        title: getInviteValueOrDefault(invitation, 'inviteeTitle') || null,
        password: '',
        phoneNumber: getInviteValueOrDefault(invitation, 'inviteePhoneNumber'),
    });
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = useAppDispatch();
    const setRegisterField = (field: keyof RegisterData, value: string) => {
        setRegisterData({ ...registerData, [field]: value });
    };

    const onRegister = async () => {
        setIsSaving(true);
        try {
            await register(registerData);

            await loadUser(dispatch);
            if (onRegisterCallback) onRegisterCallback();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Register
            isSaving={isSaving}
            onRegister={onRegister}
            registerData={registerData}
            setRegisterField={setRegisterField}
            countryCode={invitation?.countryCode}
        />
    );
};

interface RegisterProps {
    registerData: RegisterData;
    countryCode?: keyof typeof COUNTRY_CODES;
    setRegisterField: (field: keyof RegisterData, value: string) => void;
    isSaving: boolean;
    buttonLabel?: TranslatableText;
    onRegister: () => void;
}

const Register: React.FC<RegisterProps> = ({
    buttonLabel = <Trans>Register</Trans>,
    registerData,
    countryCode,
    setRegisterField,
    onRegister,
    isSaving,
}) => {
    const [confirmPassword, setConfirmPassword] = useState(registerData.password);
    const [emailValidity, setEmailValidity] = useState<EmailValidity>(EmailValidity.NotChecked);
    const validator = useMemo(() => new PasswordValidator(getDefaultPasswordRequirements()), []);

    const currentLanguagePhoneLang = useGetCorrectLanguageCodeForPhoneInput();
    const validationManager = useValidationManager([
        {
            isValid: (value, options) => registerData.password === confirmPassword,
            message: ({ field }) => <Trans>Passwords must match</Trans>,
            summaryMessage: ({ field }) => <Trans>Passwords must match</Trans>,
        },
        {
            isValid: (value, options) => validator.validate(registerData.password).valid,
            message: ({ field }) => <Trans>Password does not fulfill complexity rules</Trans>,
            summaryMessage: ({ field }) => <Trans>Password does not fulfill complexity rules</Trans>,
        },
    ]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        onRegister();
    };

    const checkEmail = async () => {
        if (registerData.email.length === 0) {
            setEmailValidity(EmailValidity.NotChecked);
            return;
        }
        try {
            setEmailValidity(EmailValidity.Checking);
            const validity = await checkRegisterEmailAddress(registerData.email);
            setEmailValidity(validity);
        } catch (e: any) {
            console.error(e);
        }
    };
    return (
        <div className={theme.container}>
            <ValidationForm validationManager={validationManager} onSubmit={handleFormSubmit}>
                <RegisterFormEmailWrapper emailValidity={emailValidity}>
                    <UserPageInput
                        disabled={!!registerData.inviteToken}
                        name="email"
                        required
                        onBlur={checkEmail}
                        type="email"
                        label={<Trans>Email</Trans>}
                        value={registerData.email}
                        onChange={(e) => setRegisterField('email', e.target.value)}
                        autoFocus
                        className={classnames({
                            'is-invalid':
                                emailValidity === EmailValidity.BadEmail || emailValidity === EmailValidity.AlreadyExists,
                        })}
                    />
                </RegisterFormEmailWrapper>

                {isSalutatable(countryCode) && (
                    <>
                        <UserPageSelect
                            name="salutation"
                            label={t`Salutation`}
                            value={registerData.salutation ?? ''}
                            onChange={(e) => setRegisterField('salutation', e.target.value)}>
                            <option value="">{t`None`}</option>
                            {USER_SALUTATION_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </UserPageSelect>

                        <UserPageSelect
                            name="title"
                            label={t`Title`}
                            value={registerData.title ?? ''}
                            onChange={(e) => setRegisterField('title', e.target.value)}>
                            <option value="">{t`None`}</option>
                            {USER_TITLE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </UserPageSelect>
                    </>
                )}

                <UserPageInput
                    name="fname"
                    required
                    type="text"
                    label={<Trans>First name</Trans>}
                    value={registerData.firstName}
                    onChange={(e) => setRegisterField('firstName', e.target.value)}
                />

                <UserPageInput
                    name="lname"
                    required
                    type="text"
                    label={<Trans>Last name</Trans>}
                    value={registerData.lastName}
                    onChange={(e) => setRegisterField('lastName', e.target.value)}
                />
                <UserPageInputWrapper label={<Trans>Phone number</Trans>}>
                    <PhoneInput
                        value={registerData.phoneNumber}
                        onChange={(value) => setRegisterField('phoneNumber', value)}
                        required
                        fieldName={t`Phone number`}
                        initialCountry={countryCode?.toLowerCase() ?? currentLanguagePhoneLang}
                    />
                </UserPageInputWrapper>

                <UserPageInput
                    required
                    type="password"
                    minLength={6}
                    label={<Trans>Password</Trans>}
                    value={registerData.password}
                    onChange={(e) => setRegisterField('password', e.target.value)}
                />

                <UserPageInput
                    required
                    type="password"
                    label={<Trans>Repeat password</Trans>}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <PasswordValidation password={registerData.password} validator={validator} />

                <ValidationSummary />

                <RubusButton size="lg" block type="submit" color="primary" loading={isSaving}>
                    {buttonLabel}
                </RubusButton>
            </ValidationForm>
        </div>
    );
};
export default Register;
